import { useEffect, useState } from 'react';
import { Clock, TriangleAlert } from 'lucide-react';
import type { CreatedMcpToken } from '../api/client';
import { CopyButton } from './CopyButton';
import { Alert } from './Feedback';

/** How long the plaintext token stays on screen before it is destroyed. */
const VISIBLE_SECONDS = 60;

/**
 * The single appearance of a freshly minted token.
 *
 * The server returns the plaintext exactly once, so this is the only chance to
 * copy it. The value is held in component state and cleared when the countdown
 * ends or the panel closes, rather than being left in the page for the rest of
 * the session -- an unattended screen should not keep a working credential on
 * display indefinitely.
 */
export function OneTimeToken({
  created,
  onFinished,
}: {
  created: CreatedMcpToken;
  /** Called once when the plaintext is discarded, by timer or by dismissal. */
  onFinished(): void;
}) {
  const [token, setToken] = useState(created.token);
  const [secondsLeft, setSecondsLeft] = useState(VISIBLE_SECONDS);

  // Clears the value and notifies the page in one step, so the timer path and
  // the dismiss path cannot drift apart. Guarded by the state updater so it
  // fires exactly once even if both run.
  const discard = () => {
    setToken((current) => {
      if (current) onFinished();
      return '';
    });
  };

  useEffect(() => {
    if (!token) return;
    const timer = setInterval(() => {
      setSecondsLeft((previous) => {
        if (previous <= 1) {
          clearInterval(timer);
          discard();
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // Intentionally keyed on `token` alone. Re-running this effect on every
    // render would restart the countdown; `discard` only calls state setters,
    // so a stale closure over it is harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <Alert tone="warning">
        <strong>Token hidden.</strong> For your security the value was removed from this screen
        after {VISIBLE_SECONDS} seconds. It cannot be shown again — revoke this token and create
        another if you did not copy it in time.
      </Alert>
    );
  }

  return (
    <Alert tone="success" onDismiss={discard}>
      <strong>Copy this token now — it is shown only once.</strong>
      <div className="token-reveal">
        <code className="token-value">{token}</code>
        <CopyButton value={token} label="Copy token" />
      </div>
      <p className="token-countdown">
        <Clock size={13} /> Hidden in {secondsLeft}s
      </p>
      <p className="token-countdown-note">
        <TriangleAlert size={13} /> Paste it into your client before the timer ends. If you miss
        it, revoke this token and create another.
      </p>
    </Alert>
  );
}
